import { tv } from "tailwind-variants";

import type { VariantProps } from "tailwind-variants";
import type { SlotsToClasses } from "../../utils/types";

export const radioVariants = tv({
  slots: {
    // RadioGroup container
    group: ["flex flex-col gap-2"],

    // Group label (14px semibold, text-primary)
    groupLabel: ["text-sm font-semibold leading-5 text-primary"],

    // Wrapper for radio items (used for horizontal layout)
    radiosWrapper: ["flex flex-col gap-2"],

    // Radio item wrapper
    radio: [
      "group relative flex cursor-pointer",
      "transition-colors duration-150 ease-out",
      // Disabled state
      "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-100",
    ],

    // Radio content wrapper (contains label + inner)
    radioContent: ["flex flex-1 flex-col gap-1"],

    // Per-radio label (12px semibold, text-secondary)
    radioLabel: ["text-xs font-semibold leading-4 text-secondary"],

    // Radio inner wrapper (contains indicator + labelWrapper)
    radioInner: ["flex items-center gap-2"],

    // Radio circle (16px × 16px) TODO(jeremys): use appropriate tailwind color / tokens as appropriate - I think I ran into an issue with border vs. ring here so look into this
    indicator: [
      "relative flex size-4 flex-shrink-0 items-center justify-center rounded-full",
      "bg-white transition-all duration-150 ease-out",
      // Default state - gray ring (using ring instead of border for consistency)
      "ring-1 ring-inset ring-gray-150",
      // Hover state - blue ring (but not when disabled)
      "group-data-[hovered]:ring-2 group-data-[hovered]:ring-blue-500 group-data-[hovered]:ring-offset-0",
      // Selected state - blue ring, white bg (but not when disabled)
      "group-data-[selected]:bg-white group-data-[selected]:ring-1 group-data-[selected]:ring-blue-500",
      // Focus visible state
      "group-data-[focus-visible]:ring-2 group-data-[focus-visible]:ring-gray-900 group-data-[focus-visible]:ring-offset-2",
      // Disabled state - gray ring and bg (MUST come after selected to override)
      "group-data-[disabled]:!bg-gray-100 group-data-[disabled]:!ring-1 group-data-[disabled]:!ring-gray-300",
      "group-data-[disabled]:group-data-[hovered]:!ring-1 group-data-[disabled]:group-data-[hovered]:!ring-gray-300",
    ],

    // Inner selected dot (8px × 8px)
    dot: [
      "size-2 rounded-full transition-transform duration-150 ease-out",
      "scale-0 group-data-[selected]:scale-100",
      // Selected state - blue dot
      "group-data-[selected]:bg-control-selected",
      // Disabled + selected - grey dot (must come after selected to override)
      "group-data-[disabled]:group-data-[selected]:bg-gray-300",
    ],

    // Radio label wrapper (contains value + description)
    labelWrapper: ["flex flex-1 flex-col gap-0"],

    // Radio value text (14px regular, gray-900)
    label: ["text-sm leading-5 text-primary", "group-data-[disabled]:text-disabled"],

    // Radio description text (12px regular, gray-500)
    radioDescription: ["text-xs leading-4 text-secondary", "group-data-[disabled]:text-disabled"],
  },
  variants: {
    orientation: {
      vertical: {
        group: "flex-col",
        radiosWrapper: "flex-col",
      },
      horizontal: {
        group: "flex-col",
        radiosWrapper: "flex-row flex-wrap gap-4",
      },
    },
  },
  defaultVariants: {
    orientation: "vertical",
  },
});

export type RadioVariantProps = VariantProps<typeof radioVariants>;
export type RadioSlots = ReturnType<typeof radioVariants>;
export type RadioClassNames = SlotsToClasses<keyof RadioSlots>;
